import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, X, ChevronLeft } from "lucide-react";
import { HOME } from "@/constants/testIds";

export default function JsonViewer({ data, onClose }) {
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const area = document.createElement("textarea");
      area.value = jsonString;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      document.body.removeChild(area);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      data-testid={HOME.jsonPanel}
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 350 }}
      className="fixed inset-0 sm:inset-auto sm:top-0 sm:right-0 sm:w-[420px] sm:h-screen z-50 bg-[#050505] sm:border-l sm:border-white/8 flex flex-col"
    >
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/8 shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="sm:hidden p-1 hover:bg-white/5 rounded-md transition-colors cursor-pointer mr-1"
            data-testid="json-back-btn"
          >
            <ChevronLeft className="w-5 h-5 text-white/60" />
          </button>
          <div className="hidden sm:flex gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
          <span className="text-[10px] sm:text-[10px] font-mono uppercase tracking-[0.15em] text-white/40">response.json</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid={HOME.jsonCopy}
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-white/5 rounded-md transition-colors cursor-pointer text-[10px] font-mono text-white/40"
          >
            {copied ? (
              <><Check className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">Copiado</span></>
            ) : (
              <><Copy className="w-3.5 h-3.5" /><span>Copiar</span></>
            )}
          </button>
          <button
            onClick={onClose}
            data-testid="json-close-btn"
            className="p-1.5 hover:bg-white/5 rounded-md transition-colors cursor-pointer"
          >
            <X className="w-4 h-4 text-white/40" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3 sm:p-4 json-viewer min-h-0">
        <pre className="text-[10px] sm:text-[11px] leading-relaxed font-mono">
          <JsonSyntax json={jsonString} />
        </pre>
      </div>

      <div className="px-3 sm:px-4 py-2 border-t border-white/8 flex items-center justify-between shrink-0">
        <span className="text-[9px] font-mono text-white/20">{data?.results_count || 0} items</span>
        <span className="text-[9px] font-mono text-white/20">{(jsonString.length / 1024).toFixed(1)} KB</span>
      </div>
    </motion.div>
  );
}

function JsonSyntax({ json }) {
  const lines = json.split("\n");
  return (
    <code>
      {lines.map((line, i) => (
        <div key={i} className="flex">
          <span className="select-none text-white/15 w-6 sm:w-8 text-right mr-2 sm:mr-4 shrink-0 text-[9px] sm:text-[11px]">{i + 1}</span>
          <span className="break-all" dangerouslySetInnerHTML={{ __html: colorize(line) }} />
        </div>
      ))}
    </code>
  );
}

function colorize(line) {
  return line
    .replace(/"([^"]+)":/g, '<span style="color:#4ADE80">"$1"</span>:')
    .replace(/: "([^"]*)"/g, ': <span style="color:#93C5FD">"$1"</span>')
    .replace(/: (\d+)/g, ': <span style="color:#FBBF24">$1</span>')
    .replace(/: (true|false)/g, ': <span style="color:#F472B6">$1</span>')
    .replace(/: (null)/g, ': <span style="color:#6B7280">$1</span>');
}
